/**
 * Decode the HTML entities that arrive inside plain-text event fields.
 *
 * ## Where they come from
 *
 * Edumate and the LISS feed hand over text that has been through an HTML
 * escaper on the way out, so a name stored as `Raphaëlle` reaches this app as
 * `Rapha&#235;lle`. React escapes what it renders, correctly and by design — so
 * the entity is shown rather than resolved, and the calendar reads
 * "Rapha&#235;lle 1st weekly lesson". Every accented name in the timetable is
 * affected, which is most of the Music department.
 *
 * ## Why not `innerHTML` on a detached element
 *
 * The textarea trick (`el.innerHTML = value; return el.value`) decodes
 * everything and is two lines. It also runs the HTML parser over text that came
 * from outside, and a value containing `</textarea>` leaves the state the trick
 * depends on. The parse is a detour: nothing here needs HTML, only the entities
 * inside text that is otherwise plain, and a table plus a regex does that with
 * no parser involved and no DOM to depend on.
 *
 * ## One pass, deliberately
 *
 * `&amp;#235;` decodes to the literal text `&#235;` and stops there. Running
 * again would turn it into `ë`, and would equally turn a title somebody
 * genuinely typed as `&#235;` into a letter they did not write. Whatever the
 * source escaped once is decoded once.
 *
 * ## Plain text only
 *
 * Never run this over `Description.Long`, which really is HTML and is rendered
 * as such — decoding it would turn escaped markup back into live markup.
 */

/**
 * The named entities worth carrying.
 *
 * The full HTML5 list is about 2,200 names, nearly all of them mathematical,
 * and shipping it to decode a handful of French and German names in a school
 * timetable is not a trade worth making. This is the five structural ones, the
 * punctuation a word processor substitutes on the way in, and the accented
 * letters that actually occur in student and staff names here. Anything not
 * listed is left exactly as it arrived rather than guessed at.
 */
const NAMED: Record<string, string> = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: '"',
	apos: "'",
	nbsp: " ",
	// Punctuation Word and the CMSes substitute.
	lsquo: "‘",
	rsquo: "’",
	ldquo: "“",
	rdquo: "”",
	ndash: "–",
	mdash: "—",
	hellip: "…",
	deg: "°",
	// Accented letters that turn up in names on this timetable.
	agrave: "à",
	aacute: "á",
	acirc: "â",
	auml: "ä",
	aring: "å",
	ccedil: "ç",
	egrave: "è",
	eacute: "é",
	ecirc: "ê",
	euml: "ë",
	igrave: "ì",
	iacute: "í",
	icirc: "î",
	iuml: "ï",
	ntilde: "ñ",
	ograve: "ò",
	oacute: "ó",
	ocirc: "ô",
	ouml: "ö",
	oslash: "ø",
	ugrave: "ù",
	uacute: "ú",
	ucirc: "û",
	uuml: "ü",
	szlig: "ß",
	Agrave: "À",
	Aacute: "Á",
	Auml: "Ä",
	Ccedil: "Ç",
	Egrave: "È",
	Eacute: "É",
	Euml: "Ë",
	Iuml: "Ï",
	Ntilde: "Ñ",
	Ouml: "Ö",
	Uuml: "Ü",
};

/** `&name;`, `&#1234;` or `&#xA9;`. */
const ENTITY = /&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi;

/**
 * The code point a numeric entity names, or null if it names nothing usable.
 *
 * Lone surrogates and anything past the Unicode ceiling are refused rather than
 * thrown: `String.fromCodePoint` raises on both, and one malformed entity in
 * one title must not take down the render of a month of events.
 */
function codePointOf(body: string): string | null {
	const hex = body[1] === "x" || body[1] === "X";
	const digits = hex ? body.slice(2) : body.slice(1);
	const code = Number.parseInt(digits, hex ? 16 : 10);
	if (!Number.isFinite(code)) return null;
	if (code <= 0 || code > 0x10ffff) return null;
	if (code >= 0xd800 && code <= 0xdfff) return null;
	return String.fromCodePoint(code);
}

/** Decode entities in a plain-text value. Anything unrecognised is left alone. */
export function decodeEntities(value: string): string {
	/* The common case by a wide margin — most titles hold no `&` at all — and it
	   is worth skipping the regex for, since this runs over every event in a
	   month and a busy month here is around 8,000 of them. */
	if (!value || !value.includes("&")) return value;

	return value.replace(ENTITY, (match, body: string) => {
		if (body[0] === "#") return codePointOf(body) ?? match;
		return NAMED[body] ?? match;
	});
}
